import { API_BASE_URL } from "./api";

/** The backend -> device event envelope. */
export interface ServerEvent {
  type: string;
  at: string;
  data: Record<string, unknown>;
}

type Listener = (event: ServerEvent) => void;

/**
 * The persistent device channel.
 *
 * A live socket is what makes this device reachable for call fan-out, so it reconnects on drop rather
 * than giving up: an app that silently stops ringing is worse than one that is briefly offline.
 */
export class DeviceSocket {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private closedByUs = false;
  private reconnectDelayMs = 500;
  private heartbeat: number | null = null;

  constructor(private readonly token: string, private readonly deviceUuid: string) {}

  connect(): void {
    this.closedByUs = false;
    const base = API_BASE_URL.replace(/^http/, "ws");
    const url = `${base}/ws/v1/device?token=${encodeURIComponent(this.token)}&deviceUuid=${encodeURIComponent(
      this.deviceUuid
    )}`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectDelayMs = 500;
      // A periodic ping keeps intermediaries from dropping an idle socket.
      this.heartbeat = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send("ping");
        }
      }, 25_000);
    };
    socket.onmessage = (message) => {
      try {
        const event = JSON.parse(message.data as string) as ServerEvent;
        this.listeners.forEach((listener) => listener(event));
      } catch {
        // A malformed frame is not worth tearing the connection down for.
      }
    };
    socket.onclose = () => {
      this.stopHeartbeat();
      if (!this.closedByUs) {
        window.setTimeout(() => this.connect(), this.reconnectDelayMs);
        this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 10_000);
      }
    };
  }

  close(): void {
    this.closedByUs = true;
    this.stopHeartbeat();
    this.socket?.close();
    this.socket = null;
  }

  onEvent(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      window.clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }
}
